import { getCartErrorMessage } from '@theme/cart-feedback';

// <s-quick-add>: card-level add-to-cart. Adds through Shopify.actions.updateCart so the
// header badge and cart drawer refresh in place, then plays the "moon" flight: a glowing
// pumpkin orb arcs from the button into the header cart, which flares on arrival.
class SQuickAdd extends HTMLElement {
  #ac;
  #busy = false;
  #resetTimer;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      // Without Standard Actions the native product form posts to the cart instead.
      if (!window.Shopify?.actions?.updateCart) return;
      event.preventDefault();
      if (this.#busy) return;
      this.#add(form);
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#resetTimer);
  }

  async #add(form) {
    const variantId = String(new FormData(form).get('id') || '').trim();
    if (!variantId) return;

    const button = form.querySelector('button[name="add"]');
    this.#busy = true;
    this.#showError('');
    this.setAttribute('aria-busy', 'true');
    button?.classList.add('opacity-60', 'cursor-wait');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'product', detail: { source: 's-quick-add', productId: this.dataset.productId } } }
      );
      const message = result?.userErrors?.[0]?.message;
      if (message) {
        this.#showError(message);
      } else {
        this.#celebrate(button);
      }
    } catch (error) {
      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      button?.classList.remove('opacity-60', 'cursor-wait');
    }
  }

  #celebrate(button) {
    if (button) {
      button.setAttribute('data-added', '');
      clearTimeout(this.#resetTimer);
      this.#resetTimer = setTimeout(() => button.removeAttribute('data-added'), 1800);
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const ease = getComputedStyle(document.documentElement).getPropertyValue('--ease-drawer').trim() || 'cubic-bezier(0.32, 0.72, 0, 1)';
    if (button) {
      button.animate(
        [
          { boxShadow: '0 0 0 0 var(--color-accent)' },
          { boxShadow: '0 0 28px 6px color-mix(in srgb, var(--color-accent) 70%, transparent)' },
          { boxShadow: '0 0 0 0 transparent' },
        ],
        { duration: 700, easing: ease }
      );
    }

    const target = this.#cartTarget();
    if (!button || !target) return;

    const from = button.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const startX = from.left + from.width / 2;
    const startY = from.top + from.height / 2;
    const dx = to.left + to.width / 2 - startX;
    const dy = to.top + to.height / 2 - startY;
    const lift = Math.min(160, Math.abs(dy) * 0.35 + 60);

    const moon = document.createElement('span');
    moon.setAttribute('aria-hidden', 'true');
    moon.className = 'pointer-events-none fixed z-[1000] -ms-3 -mt-3 size-6 rounded-full bg-accent shadow-[0_0_24px_8px_color-mix(in_srgb,var(--color-accent)_65%,transparent)]';
    moon.style.left = `${startX}px`;
    moon.style.top = `${startY}px`;
    document.body.append(moon);

    const flight = moon.animate(
      [
        { transform: 'translate(0, 0) scale(0.4)', opacity: 0 },
        { transform: `translate(${dx * 0.15}px, ${-lift * 0.6}px) scale(1.15)`, opacity: 1, offset: 0.2 },
        { transform: `translate(${dx * 0.55}px, ${dy * 0.35 - lift}px) scale(1)`, opacity: 1, offset: 0.55 },
        { transform: `translate(${dx}px, ${dy}px) scale(0.35)`, opacity: 0.9 },
      ],
      { duration: 820, easing: ease, fill: 'forwards' }
    );

    flight.finished
      .then(() => {
        moon.remove();
        const arrived = this.#cartTarget();
        arrived?.animate(
          [
            { transform: 'scale(1) rotate(0deg)', filter: 'drop-shadow(0 0 0 transparent)' },
            { transform: 'scale(1.3) rotate(-10deg)', filter: 'drop-shadow(0 0 10px var(--color-accent))', offset: 0.35 },
            { transform: 'scale(0.95) rotate(6deg)', filter: 'drop-shadow(0 0 6px var(--color-accent))', offset: 0.7 },
            { transform: 'scale(1) rotate(0deg)', filter: 'drop-shadow(0 0 0 transparent)' },
          ],
          { duration: 600, easing: ease }
        );
      })
      .catch(() => moon.remove());
  }

  #cartTarget() {
    const trigger = document.getElementById('header-cart-trigger');
    if (!trigger) return null;
    const rect = trigger.getBoundingClientRect();
    return rect.width > 0 ? trigger : null;
  }

  #showError(message) {
    const error = this.querySelector('[data-quick-add-error]');
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }
}

if (!customElements.get('s-quick-add')) {
  customElements.define('s-quick-add', SQuickAdd);
}
